/**
 * WordDoors — the chip row a caption or hover label lays its words out as (board `WordsAsDoors`,
 * validated 2026-09-18: "the doors are CHIPS in the ratio chip's dress" — the same dress `RatioFold`'s
 * own ratio chip already wears in the same bar, shared by CSS-module composition rather than copied
 * values, so a pick and a word tap read as one gesture wherever a chip sits: the armed caption, the
 * sheet's cap, a tile's hover label, a take's caption.
 *
 * A chip's accessible name states what the tap does — `More like <word>` — and carries no visible
 * lead-in (board decision, 2026-09-18: "the tap explains itself: the word lands in the field with
 * ×"). The discreet pack-page link, when `href` is given, sits OUTSIDE the chip row as a fixed-width
 * sibling — dim, never underlined, a real `<Link>` so a crawler reads it in the caller's own DOM
 * (never a hidden anchor) — and, being outside `.chips`, it is never itself the thing a narrow row
 * clips away. `arrowReveal`/`arrowLabel` are `Tile.tsx`'s own knobs — see their own doc below.
 *
 * **Narrow rows — the wrap-and-clip, in CSS alone** (revision 2026-09-19, replacing a
 * `ResizeObserver`/`getBoundingClientRect` pass that ran on every Wall render, since `Wall.tsx`
 * rebuilds `words` fresh through `chains.captionDoors` on every keystroke — layout thrash at the
 * wall's scale, and a chip once dropped could never come back once the cell widened again). `.chips`
 * is an ordinary wrapping flex row pinned to exactly one chip's own height (`$chip-h`,
 * `WordDoors.module.scss`) with `overflow: hidden`: a chip that does not fit on the first line wraps
 * to a second, which the fixed height clips away whole — never a chip cut open mid-word, and the
 * clip reverses itself the instant CSS has more room, with no JS and no re-render. Server markup is
 * client markup; every chip is always in the DOM, which is also what a crawler with no JS reads.
 *
 * One gap this trades away, left in CSS's hands: a chip wrapped onto the clipped second line stays
 * in the tab order while its cell is active (`Tile.tsx`'s hover/focus gate sets every chip's
 * `tabIndex` alike, and no selector exists that could single out "whichever chips landed on a line
 * CSS decided to clip" — that fact is only known at layout time). The earlier JS pass avoided this
 * by never mounting a dropped chip at all; nothing in CSS reproduces that without reintroducing the
 * measurement this revision removes. Narrow enough in practice — hover/focus plus Tab plus a chip
 * that actually wrapped — that it is recorded here rather than solved.
 */
import {Link} from 'react-router'
import type {Door} from './chains'
import styles from './WordDoors.module.scss'

/**
 * The literal, unscoped marker class `Tile.module.scss` keys its own `:hover`/`:focus-within`
 * override on (operator, 2026-09-19: the tile's hover-label ↗ "must not appear on mouse hover…
 * revealed only on keyboard focus"). CSS Modules give neither file a way to reach the other's own
 * scoped classname — `Tile.module.scss` cannot select WordDoors' `.arrow`, and this file cannot
 * select `Tile.module.scss`'s `.tile` — so a plain, literal, cross-file string is the honest
 * contract, the same shape `data-wall-surface="sheet"` already is between `Wall.tsx` and every
 * surface-dependent stylesheet in this directory. Exported so both sides read the one definition.
 */
export const ARROW_FOCUS_ONLY_CLASS = 'wall-arrow-focus-only'

export type WordDoorsProps = {
    doors: readonly Door[]
    onTap: (door: Door) => void
    /** The discreet pack-page link after the chips — omitted or `null` renders none. */
    href?: string | null
    /** Reachable via Tab only while set (`Tile.tsx`'s hover/focus gate, `doorTabIndex`) — omitted,
     *  the browser's own default tab order stands (Column/Sheet, always open). */
    tabIndex?: number
    /** Bounds the whole row (chips + the discreet ↗) to the caller's own box — a caller that does
     *  not constrain it (Column/Sheet) gets a row that shrinks to its own content, centred by the
     *  caller; `Tile.tsx`'s `.tileChips` instead bounds it to the cell's own narrow width, so
     *  `.chips` actually wraps and the fixed-height clip below has something to clip. */
    className?: string
    /** The chip's own font-size (operator, 2026-09-19): `'caption'` — the armed column and the
     *  sheet cap, generous width — reads one step larger than `'hover'`'s default, a tile's own
     *  strip, which keeps `chip-dress`'s shipped 10px. */
    size?: 'caption' | 'hover'
    /** `'focus'` — `Tile.tsx`'s own hover label only — carries `ARROW_FOCUS_ONLY_CLASS`: the arrow
     *  stays in the DOM and in the server markup always (a crawler's own route to the pack page),
     *  but is visually and pointer-wise inert on a mouse hover, revealed only once keyboard focus
     *  lands anywhere in the cell (`Tile.module.scss`'s own `:focus-within` rule). The armed caption
     *  and the sheet cap (`'always'`, the default) keep their arrow visible exactly as the rest of
     *  their own row is. */
    arrowReveal?: 'always' | 'focus'
    /** The arrow's own accessible name — defaults to the generic door; `Tile.tsx` names the actual
     *  pack so a screen-reader user hears which page the link goes to, not just that one exists. */
    arrowLabel?: string
}

export function WordDoors({
    doors, onTap, href = null, tabIndex, className, size = 'hover', arrowReveal = 'always',
    arrowLabel = 'Open the pack page',
}: WordDoorsProps) {
    const chipClassName = size === 'caption' ? `${styles.chip} ${styles.chipCaption}` : styles.chip
    const arrowClassName = arrowReveal === 'focus' ? `${styles.arrow} ${ARROW_FOCUS_ONLY_CLASS}` : styles.arrow
    return (
        <span className={className ? `${styles.row} ${className}` : styles.row}>
            <span className={styles.chips}>
                {doors.map((door, i) => (
                    // Index-suffixed: two chips of the same word (a chip and the take's own cut
                    // direction can collide) must not share a React key.
                    <button
                        key={`${door.label}-${i}`}
                        type="button"
                        className={chipClassName}
                        tabIndex={tabIndex}
                        onClick={() => onTap(door)}
                        // A chip is a pill in the bar (pressed = in the bar, a tap lifts it); a typed
                        // direction searches.
                        aria-pressed={door.phrase === null ? undefined : door.pressed}
                        aria-label={door.phrase === null ? `More like ${door.label}` : door.pressed ? `Remove ${door.label}` : `Search ${door.label}`}
                    >
                        {door.label}
                    </button>
                ))}
            </span>
            {href && (
                <Link to={href} className={arrowClassName} tabIndex={tabIndex} aria-label={arrowLabel}>
                    ↗
                </Link>
            )}
        </span>
    )
}
